import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Home, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { labOrderService, labOrderItemService } from '../../lib/services.lab';
import toast from 'react-hot-toast';

interface Patient {
  id: string;
  name: string;
  unique_id: string;
  phone: string;
  date_of_birth: string;
}

interface Doctor {
  id: string;
  name: string;
}

interface ServiceItem {
  id: string;
  service_name: string;
  quantity: number;
  price: number;
  amount: number;
}

export default function InvoiceForm() {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const labOrderId = searchParams.get('lab_order_id');
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [isLabInvoice, setIsLabInvoice] = useState(false);
  const [linkedLabOrderId, setLinkedLabOrderId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    patient_id: '',
    invoice_number: '',
    issue_date: new Date().toISOString().split('T')[0],
    delivery_date: new Date().toISOString().split('T')[0],
    time: '06:00',
    ref_doctor_id: '',
    ref_commission: 0,
    receivable_account: 'Cash',
    note: '',
  });

  const [serviceItems, setServiceItems] = useState<ServiceItem[]>([
    { id: '1', service_name: '', quantity: 1, price: 0, amount: 0 }
  ]);

  const [calculations, setCalculations] = useState({
    subtotal: 0,
    discount_type: 'amount',
    discount_value: 0,
    discount_amount: 0,
    total: 0,
    amount_paid: 0,
    balance_due: 0,
  });

  useEffect(() => {
    loadPatients();
    loadDoctors();
    generateInvoiceNumber();
    if (isEditMode) {
      loadInvoice();
    } else if (labOrderId) {
      loadLabOrder(labOrderId);
    }
  }, [id, labOrderId]);

  useEffect(() => {
    calculateTotals();
  }, [serviceItems, calculations.discount_type, calculations.discount_value, calculations.amount_paid]);

  const loadPatients = async () => {
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('id, name, unique_id, phone, date_of_birth')
        .order('name');

      if (error) throw error;
      setPatients(data || []);
    } catch (error) {
      console.error('Error loading patients:', error);
    }
  };

  const loadDoctors = async () => {
    try {
      const { data, error } = await supabase
        .from('doctors')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setDoctors(data || []);
    } catch (error) {
      console.error('Error loading doctors:', error);
    }
  };

  const generateInvoiceNumber = async () => {
    try {
      const { data, error } = await supabase.rpc('generate_invoice_number');
      if (!error && data) {
        setFormData(prev => ({ ...prev, invoice_number: data }));
      } else {
        const timestamp = Date.now().toString().slice(-6);
        setFormData(prev => ({ ...prev, invoice_number: `INV-${timestamp}` }));
      }
    } catch (error) {
      const timestamp = Date.now().toString().slice(-6);
      setFormData(prev => ({ ...prev, invoice_number: `INV-${timestamp}` }));
    }
  };

  const loadLabOrder = async (orderId: string) => {
    try {
      setLoading(true);
      const order = await labOrderService.getById(orderId);
      
      if (!order) {
        toast.error('Lab order not found');
        return;
      }

      if (order.invoice_id) {
        toast.error('This lab order already has an invoice');
        navigate(`/billings/invoices/${order.invoice_id}/print`);
        return;
      }

      const orderItems = await labOrderItemService.getByOrder(orderId);

      setIsLabInvoice(true);
      setLinkedLabOrderId(orderId);

      if (order.patient) {
        setFormData(prev => ({
          ...prev,
          patient_id: order.patient!.id,
          issue_date: order.created_at.split('T')[0],
          delivery_date: order.due_at ? order.due_at.split('T')[0] : prev.delivery_date,
          ref_doctor_id: order.ordered_by || '',
          note: order.notes || '',
        }));
        setPatientSearch(order.patient.name);
      }

      if (orderItems && orderItems.length > 0) {
        const items = orderItems.map((item, index) => ({
          id: `${index + 1}`,
          service_name: item.test?.name || 'Lab Test',
          quantity: 1,
          price: item.price - item.discount,
          amount: item.price - item.discount,
        }));
        setServiceItems(items);
      }

      toast.success('Lab order loaded successfully');
    } catch (error) {
      console.error('Error loading lab order:', error);
      toast.error('Failed to load lab order');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const { data: invoice, error } = await supabase
        .from('invoices')
        .select(`
          *,
          patient:patients(id, name, unique_id, phone),
          invoice_items(*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      if (invoice) {
        setFormData({
          patient_id: invoice.patient_id,
          invoice_number: invoice.invoice_number,
          issue_date: invoice.issue_date,
          delivery_date: invoice.delivery_date || invoice.issue_date,
          time: invoice.time || '06:00',
          ref_doctor_id: invoice.ref_doctor_id || '',
          ref_commission: invoice.ref_commission || 0,
          receivable_account: invoice.receivable_account || 'Cash',
          note: invoice.note || '',
        });

        setPatientSearch(invoice.patient?.name || '');

        if (invoice.invoice_items && invoice.invoice_items.length > 0) {
          setServiceItems(invoice.invoice_items.map((item: any, index: number) => ({
            id: item.id || `${index + 1}`,
            service_name: item.service_name || item.description,
            quantity: item.quantity,
            price: item.unit_price,
            amount: item.total,
          })));
        }

        setCalculations(prev => ({
          ...prev,
          discount_amount: invoice.discount_amount || 0,
          amount_paid: invoice.amount_paid || 0,
        }));
      }
    } catch (error) {
      console.error('Error loading invoice:', error);
      alert('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    const subtotal = serviceItems.reduce((sum, item) => sum + item.amount, 0);
    
    let discountAmount = 0;
    if (calculations.discount_type === 'percentage') {
      discountAmount = (subtotal * calculations.discount_value) / 100;
    } else {
      discountAmount = calculations.discount_value;
    }

    const total = subtotal - discountAmount;
    const balanceDue = total - calculations.amount_paid;

    setCalculations(prev => ({
      ...prev,
      subtotal,
      discount_amount: discountAmount,
      total,
      balance_due: balanceDue,
    }));
  };

  const handleServiceItemChange = (index: number, field: string, value: any) => {
    const updatedItems = [...serviceItems];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value,
    };

    if (field === 'quantity' || field === 'price') {
      updatedItems[index].amount = updatedItems[index].quantity * updatedItems[index].price;
    }

    setServiceItems(updatedItems);
  };

  const addServiceItem = () => {
    setServiceItems([
      ...serviceItems,
      { id: `${serviceItems.length + 1}`, service_name: '', quantity: 1, price: 0, amount: 0 }
    ]);
  };

  const removeServiceItem = (index: number) => {
    if (serviceItems.length > 1) {
      setServiceItems(serviceItems.filter((_, i) => i !== index));
    }
  };

  const handlePatientSelect = (patient: Patient) => {
    if (isLabInvoice) {
      const confirmChange = window.confirm(
        'Warning: This invoice is linked to a lab order. Changing the patient may cause inconsistencies. Are you sure you want to continue?'
      );
      if (!confirmChange) {
        return;
      }
    }
    setFormData(prev => ({ ...prev, patient_id: patient.id }));
    setPatientSearch(patient.name);
    setShowPatientDropdown(false);
  };

  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(patientSearch.toLowerCase()) ||
    patient.unique_id?.toLowerCase().includes(patientSearch.toLowerCase()) ||
    patient.phone?.includes(patientSearch)
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.patient_id) {
      alert('Please select a patient');
      return;
    }

    if (serviceItems.length === 0 || serviceItems.every(item => !item.service_name)) {
      alert('Please add at least one service item');
      return;
    }

    try {
      setLoading(true);

      const invoiceData = {
        patient_id: formData.patient_id,
        invoice_number: formData.invoice_number,
        issue_date: formData.issue_date,
        delivery_date: formData.delivery_date,
        time: formData.time,
        ref_doctor_id: formData.ref_doctor_id || null,
        ref_commission: formData.ref_commission,
        subtotal: calculations.subtotal,
        discount_amount: calculations.discount_amount,
        total: calculations.total,
        amount_paid: calculations.amount_paid,
        balance_due: calculations.balance_due,
        receivable_account: formData.receivable_account,
        note: formData.note,
        status: calculations.balance_due === 0 ? 'paid' : calculations.amount_paid > 0 ? 'partial' : 'unpaid',
        created_by: 'Admin',
      };

      let invoiceId = id;

      if (isEditMode) {
        const { error } = await supabase
          .from('invoices')
          .update(invoiceData)
          .eq('id', id);

        if (error) throw error;

        await supabase
          .from('invoice_items')
          .delete()
          .eq('invoice_id', id);
      } else {
        const { data, error } = await supabase
          .from('invoices')
          .insert(invoiceData)
          .select()
          .single();

        if (error) throw error;
        invoiceId = data.id;
      }

      const itemsToInsert = serviceItems
        .filter(item => item.service_name)
        .map(item => ({
          invoice_id: invoiceId,
          service_name: item.service_name,
          description: item.service_name,
          quantity: item.quantity,
          unit_price: item.price,
        }));

      if (itemsToInsert.length > 0) {
        const { error: itemsError } = await supabase
          .from('invoice_items')
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }

      if (linkedLabOrderId && !isEditMode) {
        const { error: linkError } = await supabase
          .from('lab_orders')
          .update({ 
            invoice_id: invoiceId,
            payment_status: calculations.balance_due === 0 ? 'paid' : calculations.amount_paid > 0 ? 'partial' : 'pending'
          })
          .eq('id', linkedLabOrderId);

        if (linkError) {
          console.error('Error linking invoice to lab order:', linkError);
          toast.error('Invoice created but failed to link to lab order');
        } else {
          toast.success('Invoice created and linked to lab order successfully!');
        }
      } else {
        toast.success(`Invoice ${isEditMode ? 'updated' : 'created'} successfully!`);
      }

      navigate('/billings/invoices');
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} invoice`);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscountRound = () => {
    const roundedDiscount = Math.round(calculations.discount_amount);
    setCalculations(prev => ({
      ...prev,
      discount_type: 'amount',
      discount_value: roundedDiscount,
      discount_amount: roundedDiscount,
    }));
  };

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading invoice...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Home className="w-4 h-4" />
          <span>/</span>
          <span>Billings/Reception</span>
          <span>/</span>
          <span className="text-gray-900 font-medium">
            {isEditMode ? 'Edit Invoice' : 'Create Invoice'}
          </span>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {isLabInvoice && linkedLabOrderId && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Lab Order Invoice:</strong> This invoice is linked to lab order {linkedLabOrderId.substring(0, 8)}. 
                Patient and test information has been pre-filled from the lab order.
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Find Patient: <span className="text-red-500">*</span>
                {isLabInvoice && <span className="ml-2 text-xs text-blue-600">(From Lab Order)</span>}
              </label>
              <input
                type="text"
                value={patientSearch}
                onChange={(e) => {
                  setPatientSearch(e.target.value);
                  setShowPatientDropdown(true);
                }}
                onFocus={() => setShowPatientDropdown(true)}
                placeholder="Name, Phone, ID & Select"
                className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  isLabInvoice ? 'border-blue-300 bg-blue-50' : 'border-gray-300'
                }`}
                required
              />
              {showPatientDropdown && filteredPatients.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredPatients.map(patient => (
                    <div
                      key={patient.id}
                      onClick={() => handlePatientSelect(patient)}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      <div className="font-medium">{patient.name}</div>
                      <div className="text-xs text-gray-500">
                        {patient.unique_id} | {patient.phone}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice NO#</label>
              <input
                type="text"
                value={formData.invoice_number}
                onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date:</label>
              <input
                type="date"
                value={formData.issue_date}
                onChange={(e) => setFormData(prev => ({ ...prev, issue_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Date:</label>
              <input
                type="date"
                value={formData.delivery_date}
                onChange={(e) => setFormData(prev => ({ ...prev, delivery_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time:</label>
              <select
                value={formData.time}
                onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="06:00">06:00 AM</option>
                <option value="07:00">07:00 AM</option>
                <option value="08:00">08:00 AM</option>
                <option value="09:00">09:00 AM</option>
                <option value="10:00">10:00 AM</option>
                <option value="11:00">11:00 AM</option>
                <option value="12:00">12:00 PM</option>
                <option value="13:00">01:00 PM</option>
                <option value="14:00">02:00 PM</option>
                <option value="15:00">03:00 PM</option>
                <option value="16:00">04:00 PM</option>
                <option value="17:00">05:00 PM</option>
                <option value="18:00">06:00 PM</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ref Doctor:</label>
              <select
                value={formData.ref_doctor_id}
                onChange={(e) => setFormData(prev => ({ ...prev, ref_doctor_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="">Name, Phone, ID & Select</option>
                {doctors.map(doctor => (
                  <option key={doctor.id} value={doctor.id}>{doctor.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ref Com:</label>
              <select
                value={formData.ref_commission}
                onChange={(e) => setFormData(prev => ({ ...prev, ref_commission: parseFloat(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="0">--Select--</option>
                <option value="5">5%</option>
                <option value="10">10%</option>
                <option value="15">15%</option>
                <option value="20">20%</option>
              </select>
            </div>
          </div>

          <div className="mb-6">
            <table className="w-full border border-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-r">
                    Service Name <span className="text-red-500">*</span>
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-r">
                    Qty <span className="text-red-500">*</span>
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-r">
                    Price <span className="text-red-500">*</span>
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-r">
                    Amount <span className="text-red-500">*</span>
                  </th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Act</th>
                </tr>
              </thead>
              <tbody>
                {serviceItems.map((item, index) => (
                  <tr key={item.id} className="border-t">
                    <td className="px-4 py-2 border-r">
                      <input
                        type="text"
                        value={item.service_name}
                        onChange={(e) => handleServiceItemChange(index, 'service_name', e.target.value)}
                        placeholder="Select Service Name"
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-2 border-r">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleServiceItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                        min="1"
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-2 border-r">
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) => handleServiceItemChange(index, 'price', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-2 border-r">
                      <input
                        type="number"
                        value={item.amount.toFixed(2)}
                        readOnly
                        className="w-full px-2 py-1 border border-gray-300 rounded bg-gray-50"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        onClick={() => removeServiceItem(index)}
                        className="text-red-600 hover:text-red-900"
                        disabled={serviceItems.length === 1}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              onClick={addServiceItem}
              className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Add More
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
              <textarea
                value={formData.note}
                onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Sub Total</span>
                <span className="text-lg font-semibold">{calculations.subtotal.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center gap-2">
                <span className="text-sm font-medium text-red-600">Discount</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCalculations(prev => ({ ...prev, discount_type: 'percentage' }))}
                    className={`px-3 py-1 text-sm rounded ${
                      calculations.discount_type === 'percentage'
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    %
                  </button>
                  <button
                    type="button"
                    onClick={handleDiscountRound}
                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Round
                  </button>
                  <input
                    type="number"
                    value={calculations.discount_value}
                    onChange={(e) => setCalculations(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
                    min="0"
                    step="0.01"
                    className="w-24 px-2 py-1 border border-gray-300 rounded"
                  />
                </div>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Discount Amount</span>
                <span className="text-lg font-semibold">{calculations.discount_amount.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center border-t pt-2">
                <span className="text-sm font-medium text-gray-700">Total Amount:</span>
                <span className="text-lg font-semibold">{calculations.total.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Paid Amount:</span>
                <input
                  type="number"
                  value={calculations.amount_paid}
                  onChange={(e) => setCalculations(prev => ({ ...prev, amount_paid: parseFloat(e.target.value) || 0 }))}
                  min="0"
                  step="0.01"
                  className="w-32 px-2 py-1 border border-gray-300 rounded"
                />
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Due Amount:</span>
                <span className="text-lg font-semibold">{calculations.balance_due.toFixed(2)}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  Receivable Account: <span className="text-red-500">*</span>
                </span>
                <select
                  value={formData.receivable_account}
                  onChange={(e) => setFormData(prev => ({ ...prev, receivable_account: e.target.value }))}
                  className="w-32 px-2 py-1 border border-gray-300 rounded"
                  required
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank">Bank</option>
                  <option value="Card">Card</option>
                </select>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-400"
            >
              {loading ? 'Saving...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
